/**
 * ht-affiliate.js
 * Happy Tails Project — アフィリエイト支援先追跡システム
 *
 * フロー:
 *   ?ref=XXX でアクセス → /api/referral/verify で団体名を自動取得 → 緑バー表示
 *   同一セッション内の再訪 → 緑バー継続
 *   別セッション（localStorage あり、365日以内） → オレンジバー「前回と同じ支援先で宜しいですか？」
 *   localStorage なし・ログイン中 → Customer Note から復元 → オレンジバー
 *   何もなし → バー非表示・コード入力フォーム
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ht_affiliate';
  var SESSION_KEY = 'ht_affiliate_session';
  var EXPIRY_MS   = 365 * 24 * 60 * 60 * 1000; // 365日
  var API_BASE    = 'https://happytail-supporters-taq8.vercel.app/api';

  /* ── ユーティリティ ───────────────────────── */

  function getRefFromUrl() {
    try {
      var p = new URLSearchParams(window.location.search);
      return p.get('ref') || p.get('goaffpro_ref') || null;
    } catch (e) { return null; }
  }

  function saveToStorage(ref, orgName, thankYouMsg, logoUrl) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ref: ref, orgName: orgName, thankYouMsg: thankYouMsg || '', logoUrl: logoUrl || '', ts: Date.now()
      }));
    } catch (e) {}
  }

  function loadFromStorage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (Date.now() - d.ts > EXPIRY_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return d;
    } catch (e) { return null; }
  }

  function isActiveSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
  }

  function setActiveSession() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
  }

  /* ── 団体名を非同期解決 ─────────────────── */

  function resolveOrgData(ref) {
    return fetch(API_BASE + '/referral/verify?code=' + encodeURIComponent(ref))
      .then(function (res) {
        if (!res.ok) throw new Error('api error');
        return res.json();
      })
      .then(function (data) {
        if (data && data.valid && data.name) {
          return { name: data.name, thankYouMsg: data.thank_you_message || '', logoUrl: data.logo_url || '' };
        }
        throw new Error('no name');
      })
      .catch(function () {
        var map = window.htOrgMap || {};
        return { name: map[ref] || ref, thankYouMsg: '', logoUrl: '' };
      });
  }

  // 後方互換のため残す
  function resolveOrgName(ref) {
    return resolveOrgData(ref).then(function (d) { return d.name; });
  }

  /* ── クリック（訪問）記録 ─────────────────
     ?ref= でのアクセスを referral_clicks に記録する（分析の訪問数用）。
     同一セッションでは1回だけ送信し、重複カウントを防ぐ。 */
  function recordClick(ref) {
    if (!ref) return;
    try {
      var flag = 'ht_click_' + ref;
      if (sessionStorage.getItem(flag) === '1') return;
      sessionStorage.setItem(flag, '1');
    } catch (e) {}
    try {
      fetch(API_BASE + '/referral/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: ref,
          landing_path: window.location.pathname,
          referrer: document.referrer || null
        })
      }).catch(function () {});
    } catch (e) {}
  }

  /* ── Shopify Cart Attributes ─────────────── */

  function updateCartAttributes(ref, orgName, thankYouMsg, logoUrl) {
    var attrs = ref
      ? { referral_code: ref, referral_name: orgName || ref, affiliate_message: thankYouMsg || '', affiliate_logo_url: logoUrl || '' }
      : { referral_code: '', referral_name: '', affiliate_message: '', affiliate_logo_url: '' };

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }

  /* ── Customer Note自動上書きはセキュリティ理由で廃止 ──
     第三者の ?ref= リンクを踏むだけで顧客プロフィールが書き換わる問題があったため、
     localStorage / sessionStorage / cart attributes のみで紹介情報を保持する。 */
  function saveToCustomerNote() { /* no-op */ }

  /* ── アナウンスバー ─────────────────────── */

  function updateBar(orgName, isActive) {
    var bar  = document.getElementById('ht-affiliate-bar');
    var text = document.getElementById('ht-affiliate-bar-text');
    if (!bar || !text) return;

    if (!orgName) { bar.style.display = 'none'; return; }

    var tmpl = (typeof window.htAffiliateText === 'string' && window.htAffiliateText)
      ? window.htAffiliateText
      : 'あなたの支援は {org} に届きます 🐾';
    text.textContent = tmpl.replace('{org}', orgName);

    bar.style.display = '';
    bar.classList.toggle('ht-affiliate-bar--previous', !isActive);
    if (isActive) bar.classList.remove('ht-affiliate-bar--previous');
  }

  /* ── カートウィジェット ─────────────────── */

  function updateWidget(state, orgName) {
    var widget = document.getElementById('ht-cart-affiliate-widget');
    if (!widget) return;

    widget.querySelectorAll('[data-state]').forEach(function (el) {
      el.style.display = el.getAttribute('data-state') === state ? '' : 'none';
    });

    if (orgName) {
      widget.querySelectorAll('[data-org-name]').forEach(function (el) {
        el.textContent = orgName;
      });
    }
  }

  /* ── 公開 API ───────────────────────────── */

  var currentAffiliate = null;

  function applyCode(code) {
    if (!code) return false;
    var map = window.htOrgMap || {};
    var orgName = map[code];
    if (!orgName) return false;
    currentAffiliate = { ref: code, orgName: orgName };
    saveToStorage(code, orgName);
    setActiveSession();
    saveToCustomerNote(code, orgName);
    updateCartAttributes(code, orgName);
    updateBar(orgName, true);
    updateWidget('active', orgName);
    return true;
  }

  function confirmPrevious() {
    if (!currentAffiliate) return;
    setActiveSession();
    saveToStorage(currentAffiliate.ref, currentAffiliate.orgName, currentAffiliate.thankYouMsg, currentAffiliate.logoUrl);
    saveToCustomerNote(currentAffiliate.ref, currentAffiliate.orgName);
    updateCartAttributes(currentAffiliate.ref, currentAffiliate.orgName, currentAffiliate.thankYouMsg, currentAffiliate.logoUrl);
    updateBar(currentAffiliate.orgName, true);
    updateWidget('active', currentAffiliate.orgName);

    /* APIから最新データを取得してカート属性を更新 */
    var ref = currentAffiliate.ref;
    resolveOrgData(ref).then(function (d) {
      currentAffiliate = { ref: ref, orgName: d.name, thankYouMsg: d.thankYouMsg, logoUrl: d.logoUrl };
      saveToStorage(ref, d.name, d.thankYouMsg, d.logoUrl);
      updateCartAttributes(ref, d.name, d.thankYouMsg, d.logoUrl);
    });
  }

  function declineAffiliate() {
    currentAffiliate = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
    updateCartAttributes(null, null);
    updateBar(null, false);
    updateWidget('none', null);
  }

  function applyWithName(code, orgName, thankYouMsg, logoUrl) {
    if (!code || !orgName) return false;
    currentAffiliate = { ref: code, orgName: orgName, thankYouMsg: thankYouMsg || '', logoUrl: logoUrl || '' };
    saveToStorage(code, orgName, thankYouMsg, logoUrl);
    setActiveSession();
    saveToCustomerNote(code, orgName);
    updateCartAttributes(code, orgName, thankYouMsg, logoUrl);
    updateBar(orgName, true);
    updateWidget('active', orgName);
    return true;
  }

  /* ── 紹介なし購入者の支援先割当 ─────────────────
     犬/猫を選ぶと、累計支援額が最少の公開メンバーへ公平に自動割当（サーバーで確定）。
     シェルターを選ぶと予約コード 'shelter' を適用する。 */
  function assignByAnimal(type) { // type: 'dog'|'cat'
    return fetch(API_BASE + '/referral/assign?type=' + encodeURIComponent(type))
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (d) { return (d && d.referral_code) ? d : null; })
      .catch(function () { return null; });
  }

  function chooseAnimal(type) {
    assignByAnimal(type).then(function (d) {
      if (!d) return; // 失敗時は何もしない（再選択可）
      applyWithName(d.referral_code, d.public_name);
      resolveOrgData(d.referral_code).then(function (full) {
        applyWithName(d.referral_code, full.name || d.public_name, full.thankYouMsg, full.logoUrl);
      });
    });
  }

  function chooseShelter() {
    applyWithName('shelter', 'シェルター建設基金');
    resolveOrgData('shelter').then(function (full) {
      applyWithName('shelter', full.name || 'シェルター建設基金', full.thankYouMsg, full.logoUrl);
    });
  }

  window.htAffiliate = {
    get:           function () { return currentAffiliate; },
    apply:         applyCode,
    applyWithName: applyWithName,
    chooseAnimal:  chooseAnimal,
    chooseShelter: chooseShelter,
    confirm:       confirmPrevious,
    decline:       declineAffiliate
  };

  /* ── 初期化 ─────────────────────────────── */

  function init() {
    /* ── 商品ページ制御（product-form が存在する場合のみ）── */
    if (document.querySelector('product-form')) {
      // Buy Nowボタン（Shop Pay等）を非表示
      var s = document.createElement('style');
      s.textContent = '.shopify-payment-button{display:none!important}';
      document.head.appendChild(s);

      // fetchインターセプト: /cart/add 後に /cart へリダイレクト
      var _fetch = window.fetch;
      window.fetch = function (url, opts) {
        var p = _fetch.apply(this, arguments);
        if (typeof url === 'string' && /\/cart\/add/.test(url)) {
          p.then(function (r) {
            if (r.ok) setTimeout(function () { window.location.href = '/cart'; }, 150);
          });
        }
        return p;
      };
    }

    var urlRef = getRefFromUrl();

    if (urlRef) {
      /* ① 新規アフィリエイト訪問 - refコードで即時表示し、名前は非同期解決 */
      currentAffiliate = { ref: urlRef, orgName: urlRef };
      setActiveSession();
      recordClick(urlRef);
      updateBar(urlRef, true);
      updateWidget('active', urlRef);

      resolveOrgData(urlRef).then(function (d) {
        var orgName = d.name;
        var thankYouMsg = d.thankYouMsg;
        var logoUrl = d.logoUrl;
        currentAffiliate = { ref: urlRef, orgName: orgName, thankYouMsg: thankYouMsg, logoUrl: logoUrl };
        saveToStorage(urlRef, orgName, thankYouMsg, logoUrl);
        saveToCustomerNote(urlRef, orgName);
        updateCartAttributes(urlRef, orgName, thankYouMsg, logoUrl);
        updateBar(orgName, true);
        updateWidget('active', orgName);
      });

    } else if (isActiveSession()) {
      /* ② 同一セッション内の再訪 — まずキャッシュで即表示し、APIで最新データに更新 */
      var stored = loadFromStorage();
      if (stored) {
        currentAffiliate = { ref: stored.ref, orgName: stored.orgName, thankYouMsg: stored.thankYouMsg || '', logoUrl: stored.logoUrl || '' };
        updateCartAttributes(stored.ref, stored.orgName, stored.thankYouMsg, stored.logoUrl);
        updateBar(stored.orgName, true);
        updateWidget('active', stored.orgName);

        resolveOrgData(stored.ref).then(function (d) {
          currentAffiliate = { ref: stored.ref, orgName: d.name, thankYouMsg: d.thankYouMsg, logoUrl: d.logoUrl };
          saveToStorage(stored.ref, d.name, d.thankYouMsg, d.logoUrl);
          updateCartAttributes(stored.ref, d.name, d.thankYouMsg, d.logoUrl);
          updateBar(d.name, true);
          updateWidget('active', d.name);
        });
      } else {
        updateWidget('none', null);
      }

    } else {
      /* ③・④ セッション外 */
      var prev = loadFromStorage();

      if (!prev && window.htCustomerSaved) {
        /* ④ localStorage なし・ログイン中・Customer Note から復元 */
        prev = window.htCustomerSaved;
        saveToStorage(prev.ref, prev.orgName);
      }

      if (prev) {
        currentAffiliate = { ref: prev.ref, orgName: prev.orgName, thankYouMsg: prev.thankYouMsg || '', logoUrl: prev.logoUrl || '' };
        updateBar(prev.orgName, false);          // オレンジバー
        updateWidget('previous', prev.orgName);  // 確認プロンプト
        /* カート属性はユーザーが確認するまで更新しない */
      } else {
        updateWidget('none', null);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
